import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { inject } from '@angular/core';
import { IAuthAccessToken } from '@app-auth/data-access/entities/auth.interface';
import { BiometricService, IBiometricInfo } from '@app-services/biometric.service';
import { setPropError, setPropLoaded, setPropLoading, withReqState } from '@shared/stores/prop-state.store';

interface IBiometricState {
    /** Device has usable biometric hardware enrolled. */
    available: boolean;
    /** User passed biometrics in the current app session. */
    unlocked: boolean;
    /** Biometric login is turned on (persisted in localStorage). */
    enabled: boolean;
    /** Caps the cold-start auto-prompt to once per launch. */
    autoSuppressed: boolean;
}

const initialState: IBiometricState = {
    available: false,
    unlocked: false,
    enabled: false,
    autoSuppressed: false,
};

/**
 * State + orchestration for biometric login. Components, guards and the AuthStore
 * talk to this store; it delegates the native plugin work to {@link BiometricService}.
 * Async operations expose `unlock*`/`enable*` req-state signals (loading/loaded/error).
 *
 * The whole session ({@link IAuthAccessToken}) is the biometric-protected secret, so
 * the 2h access token is refreshed normally and {@link updateStoredSession} keeps the
 * Keychain copy fresh. Logout {@link lock}s (keeps biometrics); only an auth failure
 * or explicit toggle {@link disable}s it.
 */
export const BiometricStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withReqState('unlock', 'enable'),
    withMethods((
        store,
        service = inject(BiometricService),
    ) => {
        const checkAvailability = async (): Promise<boolean> => {
            const ok = await service.isAvailable();
            patchState(store, { available: ok });
            return ok;
        };

        const enable = async (session: IAuthAccessToken | null, email?: string | null): Promise<boolean> => {
            if (!service.isNative() || !session?.Token) return false;

            patchState(store, { ...setPropLoading('enable') });
            try {
                await service.verifyIdentity({
                    reason: 'Confirme sua identidade para ativar o login por biometria',
                    title: 'Login por biometria',
                    subtitle: 'Entre mais rápido na próxima vez',
                    maxAttempts: 3,
                    useFallback: true,
                });
                await service.storeSession(session);

                const info: Partial<IBiometricInfo> = { enabled: true };
                if (email) info.email = email;
                service.patchInfo(info);
                patchState(store, { enabled: true, unlocked: true, ...setPropLoaded('enable') });
                return true;
            } catch (error) {
                patchState(store, { ...setPropError('enable', error) });
                return false;
            }
        };

        const disable = async (): Promise<void> => {
            patchState(store, { unlocked: false, enabled: false });
            service.patchInfo({ enabled: false, email: null });
            await service.deleteSession();
        };

        return {
            checkAvailability,
            enable,

            /** After the first password login, offers biometrics via the native dialog. Once. */
            promptToEnable: async (session: IAuthAccessToken | null, email?: string | null): Promise<void> => {
                if (store.enabled() || !session?.Token) return;
                if (!(await checkAvailability())) return;
                await enable(session, email);
            },

            /** Prompts biometrics and returns the stored session on success. */
            unlock: async (): Promise<IAuthAccessToken | null> => {
                if (!service.isNative() || !store.enabled()) return null;

                patchState(store, { ...setPropLoading('unlock') });
                try {
                    await service.verifyIdentity({
                        reason: 'Use sua biometria para entrar',
                        title: 'Entrar com biometria',
                        maxAttempts: 3,
                        useFallback: true,
                    });
                    const session = await service.readSession();
                    patchState(store, { unlocked: true, ...setPropLoaded('unlock') });
                    return session;
                } catch (error) {
                    patchState(store, { ...setPropError('unlock', error) });
                    return null;
                }
            },

            /** Silently refreshes the stored session (no prompt) so the Keychain stays current. */
            updateStoredSession: async (session: IAuthAccessToken | null): Promise<void> => {
                if (!service.isNative() || !store.enabled() || !session?.Token) return;
                try {
                    await service.storeSession(session);
                } catch {
                }
            },

            /** Marks the current app session as authenticated (e.g. after a password login). */
            markUnlocked: (): void => {
                patchState(store, { unlocked: true });
            },

            /** Locks the gate (e.g. on logout) WITHOUT removing the stored credential. */
            lock: (): void => {
                patchState(store, { unlocked: false, autoSuppressed: true, enabled: service.readInfo().enabled });
            },

            /** True at most once per launch (cold start) to auto-trigger biometrics. */
            consumeAutoUnlock: (): boolean => {
                if (store.autoSuppressed() || !store.enabled()) return false;
                patchState(store, { autoSuppressed: true });
                return true;
            },

            /** Fully turns biometric login off and wipes the stored credential. */
            disable,
        };
    }),
    withHooks({
        onInit(store, service = inject(BiometricService)) {
            patchState(store, { enabled: service.readInfo().enabled });
            void store.checkAvailability();
        },
    }),
);
